/**
 * Admin V3 — AdvisoryBanner (SC-PR-11)
 *
 * Reusable banner that makes the "this is advisory only, the operator
 * decides" semantics explicit and visually prominent. Used by SC-PR-11
 * RefundGuidanceModal and reserved for SC-PR-12+ consumers (audit-log
 * recommendation visibility, etc.).
 *
 * Pattern mirrors the existing GovernanceBanner in
 * CommissionsListPage.js but with a stronger amber/warning treatment
 * because financial-decision recommendations carry more risk than a
 * commission-matrix informational note.
 *
 * Props:
 *   - lines: string[] (one paragraph per array element)
 *   - locale: optional, defaults handled by callers
 *   - tone: "advisory" (default amber) | "info" (slate) — extensible
 *   - testId: optional data-testid override
 *
 * NO side effects, NO fetches, NO state. Pure presentation.
 */

import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';

const TONES = {
  advisory: {
    container: 'rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3',
    text: 'text-[12.5px] text-amber-800',
    icon: AlertTriangle,
    iconClass: 'text-amber-600',
  },
  info: {
    container: 'rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3',
    text: 'text-[12.5px] italic text-slate-600',
    icon: Info,
    iconClass: 'text-slate-500',
  },
};

const AdvisoryBanner = ({ lines = [], tone = 'advisory', testId }) => {
  const config = TONES[tone] || TONES.advisory;
  const Icon = config.icon;
  return (
    <div className={config.container} data-testid={testId}>
      <div className="flex items-start gap-2">
        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${config.iconClass}`} />
        <div className={config.text}>
          {lines.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdvisoryBanner;
